import "@/styles/globals.css";
import App, { AppContext, AppProps } from "next/app";
import { Provider } from "react-redux";
import { persistor, store } from "@/redux/store";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ClientConfigProvider } from "@/context/ConfigContext";
import MainLayout from "@/layout/MainLayout/MainLayout";
import { Config } from "@/types/Config";
import { PersistGate } from "redux-persist/integration/react";
import Maintenance from "@/components/Maintenance/Maintenance";
import { SessionProvider } from "next-auth/react";
import SessionErrorWatcher from "@/hooks/SessionErrorWatcher";
import { SessionExpirationProvider } from "@/context/SessionExprationContext";
import { AuthDialogProvider } from "@/context/AuthProvider/AuthDialogContext";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import LoadingSpinner from "@/components/Loaders/LoadingSpinner/LoadingSpinner";

interface MyAppProps extends AppProps {
  config: Config;
}

export default function MyApp({
  Component,
  pageProps,
  config,
}: Readonly<MyAppProps>) {
  const router = useRouter();

  const [loading, setLoading] = useState(false);
  const prevPathRef = useRef<string>(router.asPath);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: false,
          },
        },
      }),
  );

  useEffect(() => {
    const handleStart = (url: string) => {
      const currentUrl = prevPathRef.current || "";
      if (currentUrl && typeof currentUrl === "string") {
        const currentPath = currentUrl.split("?")[0];
        const nextPath = url.split("?")[0];

        const isHomeToSearch =
          (currentPath === "/" || currentPath === "/home") &&
          nextPath === "/search";
        const isSameExactUrl = currentUrl === url;
        const isSameSearchRoute =
          url.startsWith("/search?sid=") &&
          currentUrl.startsWith("/search?sid=");

        const disabledPaths = new Set([
          "/signin",
          "/signup",
          "/login-confirm",
          "/reset-password",
          "/confirm-password",
          "/verify-password",
          "/sessionExpired",
          "/terms-and-conditions",
          "/privacy",
        ]);

        if (
          disabledPaths.has(currentPath || "") ||
          disabledPaths.has(nextPath)
        ) {
          return;
        }

        if (isSameSearchRoute || isHomeToSearch || isSameExactUrl) {
          return;
        }

        setLoading(true);
      }
    };

    const handleComplete = (url: string) => {
      setLoading(false);
      prevPathRef.current = url;
    };

    router.events.on("routeChangeStart", handleStart);
    router.events.on("routeChangeComplete", handleComplete);
    router.events.on("routeChangeError", handleComplete);

    return () => {
      router.events.off("routeChangeStart", handleStart);
      router.events.off("routeChangeComplete", handleComplete);
      router.events.off("routeChangeError", handleComplete);
    };
  }, [router]);

  return (
    <>
      {config ? (
        <QueryClientProvider client={queryClient}>
          <ClientConfigProvider initialConfig={config}>
            <Provider store={store}>
              <SessionProvider session={pageProps.session} refetchInterval={5 * 60}>
                <SessionErrorWatcher />
                <SessionExpirationProvider>
                  <AuthDialogProvider>
                    <PersistGate loading={null} persistor={persistor}>

                      <MainLayout>
                        {loading && <LoadingSpinner />}
                        <Component {...pageProps} />
                      </MainLayout>
                    </PersistGate>
                  </AuthDialogProvider>
                </SessionExpirationProvider>
              </SessionProvider>
            </Provider>
          </ClientConfigProvider>
        </QueryClientProvider>
      ) : (
        <Maintenance />
      )}
    </>
  );
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);
  let clientConfig: any = {};

  try {
    clientConfig = (await import("@/data/config.json")).default;
  } catch (error) {
    console.error("Error loading configuration from JSON file", error);
  }

  return {
    ...appProps,
    config: clientConfig,
  };
};
